import {useEffect, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import {useHardwareToken} from '@hooks/useHardwareToken';
import {selectAccessToken} from '@components/Login/userSlice';
import {
    selectIsHardwareTokenDialogOpen,
    selectRecipientId,
    selectTransaction,
    setIsHardwareTokenDialogOpen,
    fetchDocumentApprovals
} from '@components/SignerUi/signerUiSlice';
import useTransactionIdFromUrl from '@components/SignerUi/hooks/useTransactionIdFromUrl';
import {getCeremonyPath} from '@constants/routes';
import IFrameEventNotifications from '@utils/iFrameNotifyer/IFrameEventNotifications';
import {
    DOCUMENT_CONFIRM_STARTED_EVENT,
    DOCUMENT_CONFIRM_SUCCESS_EVENT,
    DOCUMENT_CONFIRM_ERROR_EVENT
} from '@utils/iFrameNotifyer/events';
import {HardwareTokenDialog} from '../HardwareTokenDialog/HardwareTokenDialog';
import type {HardwareTokenError} from '../types';

export function HardwareTokenSigningContainer(): JSX.Element | null {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const {documentId} = useParams<{documentId: string}>();
    const transactionIdFromUrl = useTransactionIdFromUrl();

    const isOpen = useSelector(selectIsHardwareTokenDialogOpen);
    const accessToken = useSelector(selectAccessToken) as string;
    const recipientId = useSelector(selectRecipientId);
    const transaction = useSelector(selectTransaction);

    const transactionId = transactionIdFromUrl || transaction?.id || '';
    const currentDocumentId = documentId || '';
    const currentRecipientId = recipientId || '';
    const iFrameEventNotifications = (IFrameEventNotifications.getInstance() as unknown) as InstanceType<typeof IFrameEventNotifications>;

    const handleSuccess = useCallback(async () => {
        if (currentDocumentId) {
            await dispatch(fetchDocumentApprovals({documentId: currentDocumentId}));
        }
        await iFrameEventNotifications.postEvent(DOCUMENT_CONFIRM_SUCCESS_EVENT);
        dispatch(setIsHardwareTokenDialogOpen(false));
        if (transactionId) {
            navigate(getCeremonyPath(transactionId), {replace: true});
        }
    }, [dispatch, currentDocumentId, transactionId, navigate, iFrameEventNotifications]);

    const handleError = useCallback((errorMessage: string) => {
        console.error('[HardwareTokenSigningContainer] Error:', errorMessage);
    }, []);

    const {
        isConnected,
        isConnecting,
        certificates,
        selectedCertificate,
        setSelectedCertificate,
        isSigning,
        error,
        connect,
        disconnect,
        handleSign
    } = useHardwareToken({
        onSuccess: handleSuccess,
        onError: handleError,
        accessToken
    });

    useEffect(() => {
        if (isOpen && !isConnected && !isConnecting) {
            connect();
        }

        if (!isOpen && isConnected) {
            disconnect();
        }
    }, [isOpen, isConnected, isConnecting, connect, disconnect]);

    const handleClose = useCallback(() => {
        disconnect();
        setSelectedCertificate(null);
        dispatch(setIsHardwareTokenDialogOpen(false));
    }, [dispatch, disconnect, setSelectedCertificate]);

    const handleSignCertificate = useCallback(async (certificate: typeof selectedCertificate) => {
        if (certificate && transactionId && currentDocumentId && currentRecipientId) {
            setSelectedCertificate(certificate);
            await iFrameEventNotifications.postEvent(DOCUMENT_CONFIRM_STARTED_EVENT);
            const isSigned = await handleSign(transactionId, currentDocumentId, currentRecipientId, certificate);
            if (!isSigned) {
                await iFrameEventNotifications.postEvent(DOCUMENT_CONFIRM_ERROR_EVENT);
            }
        }
    }, [transactionId, currentDocumentId, currentRecipientId, handleSign, setSelectedCertificate, iFrameEventNotifications]);

    const handleRetry = useCallback(() => {
        setSelectedCertificate(null);
        disconnect();
        connect();
    }, [connect, disconnect, setSelectedCertificate]);

    const hasError = error !== null;
    const formattedError: HardwareTokenError | null = hasError
        ? {
            code: '100',
            type: 'CONNECTION_ERROR',
            message: error
        }
        : null;

    const isSuccess = false;

    if (!isOpen) {
        return null;
    }

    return (
        <HardwareTokenDialog
            isOpen={isOpen}
            onClose={handleClose}
            certificates={certificates}
            isCertificatesLoading={isConnecting && !isConnected}
            isConnecting={isConnecting}
            isSigning={isSigning}
            error={formattedError}
            isSuccess={isSuccess}
            onSign={handleSignCertificate}
            onRetry={handleRetry}
        />
    );
}

export default HardwareTokenSigningContainer;
